import AuthApi from '@apis/auth/AuthApi';
import { emailRegexp } from '@constants/regexp';
import useIsEmailSignIn from '@hooks/auth/useIsEmailSignIn';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProjectId from '@hooks/useProjectId';
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import SignInContext from '../context/SignInContext';

interface Props {
    value: string,
    onSend?: () =>void;
}

function useSendCode({ value, onSend }:Props) {
    const { t } = useTranslation('signIn');
    const projectId = useProjectId();
    const { useValue } = useContext(SignInContext);
    const [_, setCertPhoneNo] = useValue;
    const toast = useToast();
    const { startLoading, stopLoading } = useLoading();
    const isEmail = useIsEmailSignIn();
    const { mutate } = useMutation({
        onMutate: startLoading,
        mutationFn: () => {
            if (isEmail) {
                setCertPhoneNo(value);
                return AuthApi.sendEmail({
                    email: value,
                    projectId,
                });
            }
            const phoneStr = value.replace(/-/gi, '');
            setCertPhoneNo(phoneStr);
            return AuthApi.sms({ phoneNo: phoneStr, projectId });
        },
        onSuccess() {
            toast.success(t('toast.codeSent'));
            onSend?.();
        },
        onError(e) {
            toast.error(t('toast.codeRequestFailed'));
            console.error(e);
        },
        onSettled: stopLoading,
    });
    return () => {
        if (isEmail && !emailRegexp.test(value)) {
            toast.error(t('toast.invalidEmail'));
            return;
        }
        if (!isEmail && value.length !== 13) {
            toast.error(t('toast.invalidPhone'));
            return;
        }
        mutate();
    };
}

export default useSendCode;
